import { FC, memo, useCallback, useMemo, MouseEvent } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';

import { useCoHostData } from '@redux/selectors/participants';
import { useShowDataById } from '@redux/selectors/shows';
import { theme } from 'theme';
import { ParticipantStatus, TeamType } from '@typings/participant';
import { ShowType } from '@typings/show';
import { formatNumber } from '@utils/index';
import { BOTTOM_MENU_HEIGHT } from '@utils/constants';
import Avatar from '@components/avatar';
import { DefaultText, PremierText } from '@components/text';
import { AvatarSize } from '@typings/user';

const Container = styled.div`
  position: fixed;
  bottom: 16px;
  right: 16px;
  width: 80%;
  max-width: 400px;
  height: 68px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  background: ${({ theme }) =>
    `linear-gradient(
      150deg,
      ${theme.colors.red[500]} 20%,
      ${theme.colors.purple[500]} 50%,
      ${theme.colors.blue[500]} 80%
    )`};
  z-index: 1000;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 50%;
    right: 0;
    bottom: ${BOTTOM_MENU_HEIGHT}px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    max-width: 100%;
  }
`;

const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const HostsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const LeftAvatarWrapper = styled.div`
  margin-top: 12px;
  margin-left: 8px;
  z-index: 1;
`;

const RightAvatarWrapper = styled.div`
  margin-left: -8px;
  z-index: 0;
`;

const TitleContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding-left: 16px;
`;

const SpeakersContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-right: 8px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const Title = styled(PremierText)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListenersText = styled(DefaultText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  white-space: nowrap;
`;

const CloseButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  margin: -4px;
  position: relative;
  z-index: 10;
`;

type Props = {
  showId: string;
  leftSpeakerProfileId?: string;
  rightSpeakerProfileId?: string;
  onClick: () => void;
  onClose: () => void;
};

const Player: FC<Props> = ({
  showId,
  leftSpeakerProfileId,
  rightSpeakerProfileId,
  onClick,
  onClose,
}) => {
  const show = useShowDataById(showId);
  const coHostData = useCoHostData(showId);

  const isShowCoHost = useMemo(
    () =>
      show.coHostId &&
      coHostData?.status !== ParticipantStatus.Pending &&
      coHostData?.status !== ParticipantStatus.Left,
    [show.coHostId, coHostData?.status],
  );

  const handleClose = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    },
    [onClose],
  );

  return (
    <Container onClick={onClick}>
      <Content>
        <HostsContainer>
          <LeftAvatarWrapper>
            <Avatar userId={show.hostId} size={AvatarSize.Small} disabled />
          </LeftAvatarWrapper>

          {isShowCoHost && (
            <RightAvatarWrapper>
              <Avatar userId={show.coHostId} size={AvatarSize.Small} disabled />
            </RightAvatarWrapper>
          )}
        </HostsContainer>

        <TitleContainer>
          <Title>{show?.title}</Title>

          <InfoContainer>
            <ListenersText>
              {`${formatNumber(show?.listenersCount) || 0} listener${
                show?.listenersCount === 1 ? '' : 's'
              }`}
            </ListenersText>
          </InfoContainer>
        </TitleContainer>

        <SpeakersContainer>
          {!!leftSpeakerProfileId && (
            <LeftAvatarWrapper>
              <Avatar
                userId={leftSpeakerProfileId}
                size={AvatarSize.Small}
                disabled
                side={
                  show.type !== ShowType.Sideless
                    ? TeamType.Blue
                    : TeamType.Neutral
                }
              />
            </LeftAvatarWrapper>
          )}

          {!!rightSpeakerProfileId && (
            <RightAvatarWrapper>
              <Avatar
                userId={rightSpeakerProfileId}
                size={AvatarSize.Small}
                disabled
                side={
                  show.type !== ShowType.Sideless
                    ? TeamType.Red
                    : TeamType.Neutral
                }
              />
            </RightAvatarWrapper>
          )}
        </SpeakersContainer>
      </Content>

      <CloseButton onClick={handleClose}>
        <X size={24} color={theme.colors.white} />
      </CloseButton>
    </Container>
  );
};

export default memo(Player);
